import { Endpoint } from '@/Config/api/endpoint';
import { ChangelogEntry } from '@/Domain/api/Changelog/dto/ChangelogEntry.dto';
import { isChangelogEntry } from '@/Domain/api/Changelog/dto/ChangelogEntry.guard';

export const CHANGELOG_TIMEOUT_MS = 8000;

const fail = (kind: 'unreachable' | 'malformed', detail: string): never => {
  throw new Error(`Changelog feed ${kind} (${Endpoint.changelog.list}): ${detail}`);
};

export class ChangelogApi {
  // Decorative cross-origin feed on every page, so it must not hang or crash the TopBar badge.
  static list = async (): Promise<ChangelogEntry[]> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CHANGELOG_TIMEOUT_MS);
    const failTransport = (error: unknown): never => {
      const detail = error instanceof Error ? error.message : String(error);
      const aborted = `timed out after ${CHANGELOG_TIMEOUT_MS}ms`;
      return fail('unreachable', controller.signal.aborted ? aborted : detail);
    };
    try {
      const response = await fetch(Endpoint.changelog.list, {
        credentials: 'omit',
        signal: controller.signal,
      }).catch(failTransport);
      if (!response.ok) {
        throw new Error(`Failed to fetch changelog: ${response.status}`);
      }
      // fetch() resolves on headers, so the deadline must outlive them or a stalled body hangs the badge.
      const entries: unknown = await response.json().catch((error: unknown) => {
        if (error instanceof SyntaxError) return fail('malformed', error.message);
        return failTransport(error);
      });
      if (!Array.isArray(entries)) {
        return fail('malformed', 'expected an array');
      }
      const items: unknown[] = entries;
      // Array.isArray proves the shape of the list, not of its records; the popover renders the records.
      if (!items.every(isChangelogEntry)) {
        const index = items.findIndex((entry) => !isChangelogEntry(entry));
        return fail('malformed', `entry ${index} is not renderable`);
      }
      return items;
    } finally {
      clearTimeout(timer);
    }
  };
}
